import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface AdminInvite {
  id: string
  email: string
  role: string
  expiresAt: string
  usedAt: string | null
  createdAt: string
  status: 'pending' | 'expired' | 'accepted'
}

export function useAdminInvites() {
  return useQuery({
    queryKey: ['admin-invites'],
    queryFn: async (): Promise<AdminInvite[]> => {
      const { data, error } = await supabase
        .from('admin_invites')
        .select('id, email, role, expires_at, used_at, created_at')
        .order('created_at', { ascending: false })

      if (error) throw error

      const now = new Date()
      return (data || []).map((invite) => {
        let status: 'pending' | 'expired' | 'accepted' = 'pending'
        if (invite.used_at) {
          status = 'accepted'
        } else if (new Date(invite.expires_at) < now) {
          status = 'expired'
        }

        return {
          id: invite.id,
          email: invite.email,
          role: invite.role,
          expiresAt: invite.expires_at,
          usedAt: invite.used_at,
          createdAt: invite.created_at,
          status,
        }
      })
    },
    staleTime: 30_000,
  })
}

export function useRevokeInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from('admin_invites')
        .delete()
        .eq('id', inviteId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invites'] })
    },
  })
}
